import { Box } from '@mui/system';
import { FieldTemplate, LayoutItem } from 'api/models';
import { FormikValues } from 'formik';
import { FormFieldRenderer } from './FormFieldRenderer';

import { LayoutItemSubType, LayoutItemType } from 'api/models/FormTemplate';
import { get } from 'object-path';
import { DividerField } from 'routes/FormTemplateEditor/FormEditor/FieldTemplate/Fields/DividerField';
import { Button, Grid, IconButton } from '@mui/material';
import SelectableTableField from 'routes/FormTemplateEditor/FormEditor/FieldTemplate/Fields/SelectableTable/SelectableTableField';
import {
  getFieldsOfSelectableTable,
  getLayoutItemStyles,
} from 'routes/FormTemplateEditor/FormEditor/utils';
import useEvaluateConditions from 'hooks/useEvaluateConditions';
import CommentButton from './components/CommentButton';
import { useRecoilValue } from 'recoil';
import { projectState } from 'state';
import { onRepeatFieldProps } from './FormLayout';
import { Add, Delete } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

interface LayoutFieldRendererProps {
  item: LayoutItem;
  fieldTemplates: FieldTemplate[];
  formik: FormikValues;
  isReadOnly: boolean;
  externalFieldValues: {
    [key: string]: string | number | boolean | string[] | null;
  };
  onRepeatField: (props: onRepeatFieldProps) => void;
  onRepeatableFieldDelete: (props: onRepeatFieldProps) => void;
  path: string;
}

export default function LayoutFieldRenderer({
  item,
  fieldTemplates,
  formik,
  isReadOnly,
  externalFieldValues,
  onRepeatField,
  onRepeatableFieldDelete,
  path,
}: LayoutFieldRendererProps) {
  const isVisible = useEvaluateConditions({
    values: { ...externalFieldValues, ...formik.values },
    conditionGroup: item.layoutOptions?.isVisible,
  });

  const { t } = useTranslation();

  const { selectedProject } = useRecoilValue(projectState);
  if (!isVisible) {
    return <></>;
  }
  const layoutOptionsStyle = item.layoutOptions?.style

  return (
    <Box
      width="100%"
      display="flex"
      flexDirection="column"
      id="container"
      sx={getLayoutItemStyles(get(item, `layoutOptions.style`))}
    >
      {/* ROWS */}
      {item?.children?.map((row, rowIndex) => (
        <Grid
          container
          key={`layout-field-grid-${row.id}`}
          rowSpacing={1}
          columnSpacing={{
            xs: 1,
            sm: 1,
            md: 1,
          }}
          sx={{
            display: 'flex',
            justifyContent: row.layoutOptions?.style?.justifyContent ?? 'start',
          }}
        >
          {/* COLUMNS */}
          {row?.children?.map((column, columnIndex) => (
            <Grid
              key={`layout-field-column-${column.id}`}
              item
              xs={12}
              sx={{ mb: 1 }}
              md={parseInt(column.layoutOptions?.layout ?? '12')}
            >
              {column?.children?.map((item, fieldIndex) => {
                const field = fieldTemplates.find(
                  // we need to split here because of the repeatable field index
                  field =>
                    field.id === item.fieldTemplateId?.split(':')[0] ||
                    field.id === item.fieldTemplateId?.split('|')[0],
                );

                if (item.type === LayoutItemType.Container) {
                  if (item.subType === LayoutItemSubType.Layout) {
                    return (
                      <LayoutFieldRenderer
                        key={`layout-field-container-${item.id}`}
                        fieldTemplates={fieldTemplates}
                        formik={formik}
                        isReadOnly={isReadOnly}
                        item={item}
                        externalFieldValues={externalFieldValues}
                        onRepeatField={onRepeatField}
                        path={`${path}.children.${rowIndex}.children.${columnIndex}`}
                        onRepeatableFieldDelete={onRepeatableFieldDelete}
                      />
                    );
                  }
                  if (item.subType === LayoutItemSubType.SelectableTable) {
                    const fields = getFieldsOfSelectableTable({
                      layoutItems: item.children ?? [],
                      fieldTemplates,
                    });
                    return (
                      <SelectableTableField
                        fields={fields}
                        formik={formik}
                        item={item}
                        externalFieldValues={externalFieldValues}
                      />
                    );
                  }
                }

                if (item.type === LayoutItemType.Static) {
                  return (
                    <DividerField
                      key={`layout-field-divier-${item.id}`}
                      layoutItem={item}
                    />
                  );
                }

                if (!field) return null;
                return (
                  <Box
                    display={'flex'}
                    mt={1}
                    mb={1}
                    width={'100%'}
                    key={`form-field-${item.fieldTemplateId}`}
                    flexDirection={'row'}
                    alignItems={'start'}
                  >
                    <Box
                      width="100%"
                      display="flex"
                      alignItems={'flex-end'}
                      flexDirection={'column'}
                    >
                      <FormFieldRenderer
                        key={`form-field-${field.id}-item-${item.id}`}
                        field={field}
                        valueId={`${item.fieldTemplateId}`}
                        formik={formik}
                        isReadOnly={isReadOnly}
                        sx={{ mb: 1 }}
                        externalFieldValues={externalFieldValues}
                        style={layoutOptionsStyle}
                      />

                      {field.fieldAttributes.isRepeatable &&
                        item.fieldTemplateId &&
                        item.fieldTemplateId?.split(':').length < 2 && (
                          <Button
                            sx={{
                              maxWidth: 'min-content',
                              flexDirection: 'row',
                              display: 'flex',
                              mt: 1,
                            }}
                            onClick={() =>
                              onRepeatField({
                                parentPath: `${path}.children.${rowIndex}.children.${columnIndex}.children`,
                                insertIndex: fieldIndex + 1,
                                item,
                              })
                            }
                            size="small"
                          >
                            <Add fontSize="small" /> {t('forms.repeat')}
                          </Button>
                        )}
                    </Box>
                    {field.fieldAttributes.isRepeatable &&
                      item.fieldTemplateId &&
                      parseInt(item.fieldTemplateId?.split(':')?.[1]) > 0 && (
                        <IconButton
                          size="small"
                          sx={{ ml: 1, mt: 2 }}
                          onClick={() =>
                            onRepeatableFieldDelete({
                              parentPath: `${path}.children.${rowIndex}.children.${columnIndex}.children`,
                              insertIndex: fieldIndex,
                              item,
                            })
                          }
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      )}

                    {isReadOnly &&
                      selectedProject?.configuration?.queryModuleEnabled && (
                        <CommentButton
                          fieldId={field.id}
                          value={formik.values[field.id]}
                        />
                      )}
                  </Box>
                );
              })}
            </Grid>
          ))}
        </Grid>
      ))}
    </Box>
  );
}
